import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from 'src/utils/axios';
//
import { useLoginAdminMutation, useRefreshJwtMutation } from 'src/graphql';
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const Refresh_KEY = 'refreshToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { mutateAsync } = useLoginAdminMutation();
  const { mutateAsync: mutateRefreshJwtAsync } = useRefreshJwtMutation();

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      const refreshToken = localStorage.getItem(Refresh_KEY);
      const accessUserRaw = localStorage.getItem('accessUser');
      const accessUser: AuthUserType = accessUserRaw ? JSON.parse(accessUserRaw) : null;

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, refreshToken, accessUser);

        dispatch({
          type: Types.INITIAL,
          payload: {
            user: accessUser,
          },
        });
      } else if (refreshToken) {
        const newTokens = await refreshJwt(refreshToken);
        if (newTokens?.accessToken) {
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: newTokens.user,
            },
          });
        } else {
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const data = {
      email,
      password,
    };

    const result = await mutateAsync(data);

    const { loginAdmin } = result;

    setSession(loginAdmin?.token ?? '', loginAdmin?.refreshToken ?? '', loginAdmin?.user ?? {});

    dispatch({
      type: Types.LOGIN,
      payload: {
        user: loginAdmin?.user ?? {},
      },
    });
  }, []);

  // Refresh
  const refreshJwt = useCallback(async (refreshToken: string) => {
    try {
      const result = await mutateRefreshJwtAsync({ refreshToken });
      const { refreshJwt } = result;

      const user = JSON.parse(localStorage.getItem('accessUser') || '{}');

      setSession(refreshJwt?.accessToken ?? '', refreshJwt?.refreshToken ?? '', user);

      return {
        accessToken: refreshJwt?.accessToken,
        refreshToken: refreshJwt?.refreshToken,
        user,
      };
    } catch (error) {
      console.error('Refresh JWT error:', error);
      setSession(null, null, null);
      dispatch({ type: Types.LOGOUT });
      return null;
    }
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const data = {
        email,
        password,
        firstName,
        lastName,
      };

      const response = await axios.post('', data);

      const { accessToken, user } = response.data;

      sessionStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null, null, null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
    }),
    [login, logout, register, state.user, status]
  );

  // console.log('memoizedValue', memoizedValue);

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
